import {
    IBridgeFieldMetadata,
    IQuestion,
    Question,
    QuestionType,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { LayoutFormItem } from "components/layout/layout-form-section";
import { LayoutItemResult, toLayoutItemResult } from "components/layout/layout-items";
import { NgGrid } from "components/shared/grid";
import { Select } from "components/ui/select";
import { AdvancedSearchEntityType, NaryFormat } from "generated/graphql";
import * as React from "react";
import { useBridgeEntityType } from "store/selectors/bridge";

export type SharedIntake = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["intake"];
type SharedIntakeSection = SharedIntake["sections"][number];
type SharedIntakeLayoutItem = SharedIntakeSection["layoutItems"][number];
type SharedIntakeColumn = SharedIntakeSection["column"];
type IntakeUsers = Record<UserId, { id: UserId; name: string }>;

export interface SharedIntakeEntity {
    hoursPerWeek: number | null;
    daysNeeded: string[] | null;
}

interface IntakeEntity extends SharedIntakeEntity {
    answers: SharedIntake["answers"];
}

const naryFormatFor = (format: string | null): NaryFormat | null => {
    switch (format) {
        case NaryFormat.OnePerLine:
            return NaryFormat.OnePerLine;
        case NaryFormat.SemicolonSeparated:
            return NaryFormat.SemicolonSeparated;
        default:
            return null;
    }
};

const toLayoutItem = (item: SharedIntakeLayoutItem): LayoutItemResult =>
    toLayoutItemResult({ ...item, naryFormat: naryFormatFor(item.naryFormat) });

const ignoreCommit = (): void => undefined;

interface IntakeSources {
    questions: IQuestion[];
    users: IntakeUsers;
    entity: IntakeEntity;
    fields: IBridgeFieldMetadata[];
}

const useIntakeSources = (
    intake: SharedIntake,
    lead: SharedIntakeEntity,
): IntakeSources => {
    const { entityType } = useBridgeEntityType(AdvancedSearchEntityType.HomeCareLead);
    const questions = React.useMemo(
        () => intake.questions.map(question => Question.load(question)),
        [intake.questions],
    );
    const users = React.useMemo(
        () =>
            Object.fromEntries(intake.users.map(user => [user.id, user])) as IntakeUsers,
        [intake.users],
    );
    const entity = React.useMemo(
        () => ({ ...lead, answers: intake.answers }),
        [lead, intake.answers],
    );
    return { questions, users, entity, fields: entityType?.fields ?? [] };
};

const userQuestionFor = (
    item: LayoutItemResult,
    questions: IQuestion[],
): IQuestion | null => {
    const question = questions.find(candidate => candidate.id === item.questionId);
    return question?.questionType === QuestionType.user ? question : null;
};

const SharedUserAnswer: React.FC<{
    title: string;
    question: IQuestion;
    sources: IntakeSources;
}> = ({ title, question, sources }) => {
    const { questions, entity, users } = sources;
    if (!question.enabled(questions, entity.answers)) {
        return null;
    }
    const userId =
        entity.answers.find(answer => answer.questionId === question.id)
            ?.idReferenceAnswer ?? null;
    return (
        <Select label={title} value={userId ?? ""} disabled>
            <option value="">Unselected</option>
            {userId ? <option value={userId}>{users[userId]?.name ?? ""}</option> : null}
        </Select>
    );
};

const SharedIntakeItem: React.FC<{
    item: LayoutItemResult;
    sources: IntakeSources;
}> = ({ item, sources }) => {
    const userQuestion = userQuestionFor(item, sources.questions);
    if (userQuestion) {
        return (
            <SharedUserAnswer
                title={item.customTitle || userQuestion.title}
                question={userQuestion}
                sources={sources}
            />
        );
    }
    return (
        <LayoutFormItem
            layoutItem={item}
            entity={sources.entity}
            fields={sources.fields}
            questions={sources.questions}
            onCommitAnswer={ignoreCommit}
            onCommitField={ignoreCommit}
        />
    );
};

const IntakeSectionTile: React.FC<{
    section: SharedIntakeSection;
    sources: IntakeSources;
}> = ({ section, sources }) => {
    const layoutItems = React.useMemo(
        () => section.layoutItems.map(toLayoutItem),
        [section.layoutItems],
    );
    return (
        <InputFormBlock>
            <Header iconName={section.icon ?? undefined}>{section.title}</Header>
            <fieldset disabled>
                {layoutItems.map(item => (
                    <SharedIntakeItem key={item.id} item={item} sources={sources} />
                ))}
            </fieldset>
        </InputFormBlock>
    );
};

const IntakeColumn: React.FC<{
    column: SharedIntakeColumn;
    intake: SharedIntake;
    sources: IntakeSources;
}> = ({ column, intake, sources }) => (
    <NgGrid.Column>
        {intake.sections
            .filter(section => section.column === column)
            .map(section => (
                <IntakeSectionTile
                    key={section.key}
                    section={section}
                    sources={sources}
                />
            ))}
    </NgGrid.Column>
);

export const SharedIntake: React.FC<{
    intake: SharedIntake;
    lead: SharedIntakeEntity;
}> = ({ intake, lead }) => {
    const sources = useIntakeSources(intake, lead);
    if (intake.sections.length === 0) {
        return null;
    }
    return (
        <NgGrid>
            <IntakeColumn column="column1" intake={intake} sources={sources} />
            <IntakeColumn column="column2" intake={intake} sources={sources} />
        </NgGrid>
    );
};
